import axios from "axios";
import { ApiSuccess, saUrl } from "./api_helper";

/**
 * Plano do catálogo SaaS (Super Admin).
 * subscriptions_count vem do withCount na API SA (grandfathering DOC 02 § 2.4).
 */
export type SaPlan = {
  id: number;
  name: string;
  slug: string;
  /** Família comercial (timeline LAC-063) — só rotas SA. */
  version_family?: string | null;
  /** Quantidade de rows na mesma family+currency (lista SA). */
  versions_count?: number;
  description: string | null;
  visibility: "public" | "private" | "internal" | string;
  currency: string;
  base_price_cents: number;
  billing_interval: "month" | "monthly" | string;
  email_quota: number;
  is_trial_plan: boolean;
  is_active: boolean;
  /** Presente nas rotas SA; ausente no catálogo tenant. */
  subscriptions_count?: number;
  gateway_plan_ref?: string | null;
  mp_preapproval_plan_id?: string | null;
  created_at?: string;
  updated_at?: string;
};

/** Payload de criação — espelha StorePlanRequest (F1: currency BRL). */
export type SaPlanCreatePayload = {
  name: string;
  slug: string;
  /** Ao duplicar versão: family do plano origem (LAC-063). */
  version_family?: string | null;
  /** ID do plano que esta versão substitui no catálogo. */
  supersedes_plan_id?: number | null;
  /** Se true, desativa o plano origem no site/checkout (assinantes ficam no ID antigo). */
  deactivate_superseded?: boolean;
  description?: string | null;
  visibility: "public" | "private" | "internal" | string;
  currency?: "BRL" | string;
  base_price_cents: number;
  billing_interval?: "monthly" | string;
  email_quota: number;
  is_trial_plan?: boolean;
  is_active?: boolean;
};

/** PATCH parcial — grandfathering bloqueia preço/cota com assinantes no backend. */
export type SaPlanUpdatePayload = Partial<SaPlanCreatePayload>;

export type PaginatedMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type PlansListResponse = ApiSuccess<SaPlan[]> & {
  meta: PaginatedMeta;
  links?: unknown;
};

export type SaPlansListParams = {
  visibility?: string;
  /** true/false quando filtrar; omitir = todos */
  is_active?: boolean;
  currency?: string;
  page?: number;
  per_page?: number;
};

/**
 * GET /super-admin/plans — URL absoluta (fora de /api/v1).
 * Token SA via interceptor (sa_authUser).
 */
export const getSuperAdminPlans = (
  params?: SaPlansListParams
): Promise<PlansListResponse> => {
  const query: Record<string, string | number | boolean> = {
    currency: params?.currency || "BRL",
    per_page: params?.per_page || 50,
  };

  if (params?.page) {
    query.page = params.page;
  }
  if (params?.visibility) {
    query.visibility = params.visibility;
  }
  if (typeof params?.is_active === "boolean") {
    // Laravel boolean(): "1"/"0" ou true/false.
    query.is_active = params.is_active ? 1 : 0;
  }

  return axios.get(saUrl("/super-admin/plans"), { params: query });
};

export const getSuperAdminPlan = (id: number): Promise<ApiSuccess<SaPlan>> => {
  return axios.get(saUrl(`/super-admin/plans/${id}`));
};

/** GET /super-admin/plans/{id}/versions — timeline de grandfathering (LAC-063). */
export const getSuperAdminPlanVersions = (
  id: number
): Promise<ApiSuccess<SaPlan[]>> => {
  return axios.get(saUrl(`/super-admin/plans/${id}/versions`));
};

export const createSuperAdminPlan = (
  payload: SaPlanCreatePayload
): Promise<ApiSuccess<SaPlan>> => {
  return axios.post(saUrl("/super-admin/plans"), payload);
};

export const updateSuperAdminPlan = (
  id: number,
  payload: SaPlanUpdatePayload
): Promise<ApiSuccess<SaPlan>> => {
  return axios.patch(saUrl(`/super-admin/plans/${id}`), payload);
};

/** Limpa PayPal/MP plan ids + cache — próximo checkout reprovisiona. */
export const clearSuperAdminPlanGatewayRefs = (
  id: number
): Promise<ApiSuccess<SaPlan>> => {
  return axios.post(saUrl(`/super-admin/plans/${id}/clear-gateway-refs`));
};

/**
 * POST /super-admin/plans/bulk-deactivate (LAC-063 + LAC-195).
 * force=true só após modal SA quando há assinantes active/past_due.
 * skipped = ids já inativos; errors = mapa id→mensagem (ex.: tem assinantes sem force).
 */
export type SaPlanBulkDeactivateResult = {
  deactivated: number[];
  skipped: number[];
  errors: Record<string, string>;
};

export const bulkDeactivateSuperAdminPlans = (
  planIds: number[],
  force = false
): Promise<ApiSuccess<SaPlanBulkDeactivateResult>> => {
  return axios.post(saUrl("/super-admin/plans/bulk-deactivate"), {
    plan_ids: planIds,
    force,
  });
};
